import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Building2, Plus, Pencil, Trash2, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

export const DepartmentsAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/departments?include_inactive=true');
      if (r.data?.success) setRows(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load departments');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', is_active: true });
    setOpenDialog(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || '',
      description: d.description || '',
      is_active: d.is_active !== false,
    });
    setOpenDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/departments/${editing.id}`, form);
        toast.success('Department updated');
      } else {
        await api.post('/departments', form);
        toast.success('Department created');
      }
      setOpenDialog(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save department');
    } finally { setSaving(false); }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/departments/${d.id}`, { is_active: !d.is_active });
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/departments/${deleteTarget.id}`);
      toast.success('Department deleted');
      setDeleteTarget(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  return (
    <Card data-testid="departments-admin-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">HR · Departments</CardTitle>
          </div>
          <Button size="sm" onClick={openCreate} data-testid="add-department-btn">
            <Plus className="h-4 w-4 mr-1.5" />Add Department
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Departments power the dropdown on the Employees and Appraisal pages. Renaming a department auto-updates every employee record assigned to it. Deleting a department in use by employees is blocked until they are reassigned.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">No departments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="departments-table">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Name</th>
                  <th className="px-4 py-2 text-left font-semibold">Description</th>
                  <th className="px-4 py-2 text-right font-semibold">Employees</th>
                  <th className="px-4 py-2 text-center font-semibold">Active</th>
                  <th className="px-4 py-2 text-right font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((d) => (
                  <tr key={d.id} data-testid={`department-row-${d.id}`} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-2.5">
                      <span className="font-medium">{d.name}</span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{d.description || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {d.employees_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Users className="h-3 w-3 text-muted-foreground" />{d.employees_count}
                        </span>
                      ) : <span className="text-muted-foreground">0</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Switch
                        checked={d.is_active !== false}
                        onCheckedChange={() => toggleActive(d)}
                        data-testid={`dept-active-toggle-${d.id}`}
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(d)} data-testid={`edit-dept-${d.id}`} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => setDeleteTarget(d)}
                          data-testid={`delete-dept-${d.id}`}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Create / Edit dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="department-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Department' : 'New Department'}</DialogTitle>
            <DialogDescription>
              Add or update a department for use across employee records.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Customer Success"
                data-testid="department-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="What this department handles"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">Inactive departments are hidden from the employee dropdown.</p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="department-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-department-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Save Changes' : 'Create Department'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="department-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.employees_count > 0
                ? <span className="text-red-500">This department has {deleteTarget.employees_count} employee(s) assigned. Reassign them first — deletion will be blocked by the server.</span>
                : "This will soft-delete the department. The slug becomes available for reuse later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-dept-btn"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default DepartmentsAdmin;
